/**
 * The catalogue, without the file system.
 *
 * Split out of `i18n.ts` because there are now three readers and only one of
 * them has a disk: the server reads JSON files, the browser fetches them, and
 * the Edge Functions name keys without ever rendering one. The *keys* and the
 * substitution rule are common to all three, and a second copy of either would
 * be a message that exists on one side and not the other.
 *
 * Pure — no I/O, no DOM, no Node.
 */

export const LOCALES = ['ru', 'en'] as const;
export type Locale = (typeof LOCALES)[number];

/** Russian: the panel is operated in Kyrgyzstan and the specification is Russian. */
export const DEFAULT_LOCALE: Locale = 'ru';

export const LOCALE_COOKIE = 'locale';

/** A year: the choice is a preference, not a session, and outliving one is the point. */
const LOCALE_COOKIE_MAX_AGE = 365 * 24 * 60 * 60;

/**
 * Every message the panel can render. Adding one here without adding it to both
 * catalogues fails at boot, which is the only moment anyone is watching.
 */
export const MESSAGE_KEYS = [
  'app.name',
  'nav.newCampaign',
  'nav.history',
  'nav.logout',
  'nav.comingSoon',
  'banner.testMode',
  'login.title',
  'login.username',
  'login.password',
  'login.submit',
  'login.error.credentials',
  'login.error.missing',
  'login.error.cooldown',
  'campaign.new.title',
  'campaign.new.intro',
  'campaign.mode.legend',
  'campaign.mode.hint',
  'campaign.mode.pairs',
  'campaign.mode.pairs.hint',
  'campaign.mode.blocks',
  'campaign.mode.blocks.hint',
  'campaign.mode.file',
  'campaign.mode.file.hint',
  'campaign.pairs.label',
  'campaign.pairs.help',
  'campaign.separator.label',
  'campaign.separator.semicolon',
  'campaign.separator.comma',
  'campaign.separator.tab',
  'campaign.separator.pipe',
  'campaign.blocks.label',
  'campaign.blocks.help',
  'campaign.blocks.phones',
  'campaign.blocks.codes',
  'campaign.blocks.phonesLabel',
  'campaign.blocks.codesLabel',
  'campaign.blocks.oneBlock',
  'campaign.blocks.tooManyBlocks',
  'campaign.blocks.countMismatch',
  'campaign.blocks.reversed',
  'campaign.blocks.swap',
  'campaign.blocks.bothPhones',
  'campaign.blocks.separatorInLines',
  'campaign.import.drop',
  'campaign.import.choose',
  'campaign.import.sheet',
  'campaign.import.delimiter',
  'campaign.import.columns',
  'campaign.import.header',
  'campaign.import.imported',
  'campaign.import.valid',
  'campaign.import.invalid',
  'campaign.import.duplicate',
  'campaign.import.empty',
  'campaign.import.singleColumn',
  'campaign.import.overLimit',
  'campaign.import.notAZip',
  'campaign.import.noWorksheet',
  'campaign.import.unsupportedCompression',
  'campaign.import.combine',
  'campaign.import.replace',
  'campaign.import.append',
  'campaign.import.cancel',
  'campaign.preview.line',
  'campaign.preview.row',
  'campaign.preview.phone',
  'campaign.preview.code',
  'campaign.preview.reason',
  'campaign.preview.unpaired',
  'campaign.preview.more',
  'campaign.template.title',
  'campaign.template.label',
  'campaign.template.example',
  'campaign.template.help',
  'campaign.template.characters',
  'campaign.template.parts',
  'campaign.template.noPlaceholder',
  'campaign.template.empty',
  'campaign.template.tooLong',
  'campaign.template.headroom',
  'campaign.template.multiSegment',
  'campaign.review.title',
  'campaign.review.problems',
  'campaign.review.duplicates',
  'campaign.review.duplicatesHint',
  'campaign.review.keep',
  'campaign.review.keepFirst',
  'campaign.review.keepLast',
  'campaign.review.keepManual',
  'campaign.review.moreGroups',
  'campaign.review.overLimit',
  'campaign.review.empty',
  'campaign.review.undecided',
  'campaign.continue',
  'confirm.title',
  'confirm.intro',
  'confirm.recipients',
  'confirm.parts',
  'confirm.message',
  'confirm.distribution.title',
  'confirm.distribution.affected',
  'confirm.distribution.parts',
  'confirm.distribution.recipients',
  'confirm.distribution.subtotal',
  'confirm.acknowledge',
  'confirm.send',
  'confirm.back',
  'confirm.notAcknowledged',
  'confirm.notDraft',
  'confirm.modal.title',
  'confirm.modal.body',
  'confirm.modal.confirm',
  'confirm.modal.cancel',
  'status.title',
  'status.recipients',
  'status.accepted',
  'status.delivered',
  'status.failed',
  'status.draft',
  'status.sending',
  'status.completed',
  'status.completedWithErrors',
  'status.cancelled',
  'status.paused',
  'status.failedCampaign',
  'status.stop',
  'status.resume',
  'status.retryFailed',
  'status.fatalResume',
  'status.fatalUnknown',
  'table.title',
  'table.search',
  'table.searchPlaceholder',
  'table.searchSubmit',
  'table.empty',
  'table.showing',
  'table.parts',
  'table.message',
  'table.show',
  'table.page',
  'table.previous',
  'table.next',
  'table.filter.all',
  'table.filter.accepted',
  'table.filter.failed',
  'table.filter.multipart',
  'table.status',
  'table.error',
  'table.delivery',
  'status.refresh',
  'status.export',
  'recipient.pending',
  'recipient.sending',
  'recipient.accepted',
  'recipient.delivered',
  'recipient.notDelivered',
  'recipient.expired',
  'recipient.failed',
  'history.title',
  'history.empty',
  'history.id',
  'history.when',
  'history.status',
  'history.parts',
  'history.testMode',
  'history.partsDisagree',
  'history.backup',
  'send.0',
  'send.1',
  'send.2',
  'send.3',
  'send.4',
  'send.5',
  'send.6',
  'send.7',
  'send.8',
  'send.9',
  'send.10',
  'send.11',
  'send.unknown',
  'delivery.0',
  'delivery.1',
  'delivery.2',
  'delivery.3',
  'delivery.4',
  'delivery.5',
  'delivery.6',
  'delivery.7',
  'delivery.unknown',
  'report.notFound',
  'report.busy',
  'transport.timeout',
  'transport.network',
  'transport.http',
  'transport.malformed',
  'row.phoneMissing',
  'row.phoneInvalidCharacters',
  'row.phoneUnsupportedCountry',
  'row.phoneWrongLength',
  'row.phoneUnknownFormat',
  'row.missingSeparator',
  'row.ambiguousSeparator',
  'row.codeMissing',
  'row.duplicate',
  'error.title',
  'error.notFound',
  'error.methodNotAllowed',
  'error.unexpected',
  'error.staleForm',
  'error.formTooLarge',
  'error.unsupportedForm',
  'error.backHome',
  'locale.switch',
  'login.email',
  'login.error.signedOut',
  'app.loading',
  'app.loadFailed',
  'app.retry',
  'app.notConfigured',
  'history.recipients',
  'history.open',

  // Added with the panel features of C14 — see TASKS.md.
  'lookup.known',
  'lookup.badRequest',
  'lookup.badCredentials',
  'lookup.badPhone',
  'lookup.notInDatabase',
  'lookup.unrecognised',
  'lookup.unavailable',
  'lookup.title',
  'lookup.free',
  'lookup.check',
  'lookup.checking',
  'lookup.allKnown',
  'lookup.summary',
  'lookup.unknownWarning',
  'lookup.operator',
  'lookup.result',
  'health.pipeline',
  'health.running',
  'health.stopped',
  'health.stoppedHelp',
  'health.balance',
  'health.balanceParts',
  'health.balanceLow',
  'health.queued',
  'health.unavailable',
  'undo.title',
  'undo.body',
  'undo.cancel',
  'undo.now',
  'undo.cancelled',
  'fatigue.title',
  'fatigue.body',
  'fatigue.lastSent',
  'fatigue.campaign',
  'fatigue.checking',
  'templates.title',
  'templates.save',
  'templates.name',
  'templates.namePlaceholder',
  'templates.use',
  'templates.delete',
  'templates.empty',
  'templates.nameRequired',
  'templates.bodyRequired',
  'templates.confirmDelete',
  'templates.confirmUse',
  'templates.saved',
  'draft.restored',
  'draft.discard',
  'table.search',
  'table.searchPlaceholder',
  'table.searchEmpty',
  'table.clear',
  'table.export',
  'table.exporting',
  'table.exportFailed',
  'history.author',
  'history.authorUnknown',
] as const;

export type MessageKey = (typeof MESSAGE_KEYS)[number];

export type Catalogue = Readonly<Record<MessageKey, string>>;

/** Values interpolated into `{name}` placeholders. */
export type Params = Readonly<Record<string, string | number>>;

export type Translate = (key: MessageKey, params?: Params) => string;

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value);
}

/**
 * `{name}` placeholders only — no plural rules, no dates, no nesting. Anything
 * that needs more than substitution is computed by the caller, where the types
 * are still around to check it.
 */
export function interpolate(message: string, params?: Params): string {
  if (params === undefined) return message;
  return message.replace(/\{(\w+)\}/g, (whole, name: string) => {
    const value = params[name];
    return value === undefined ? whole : String(value);
  });
}

/**
 * Reads a parsed JSON catalogue, refusing one that is incomplete.
 *
 * A missing key is a blank on a screen, found by whoever is reading it. This is
 * the check that turns that into a failure at load, which is the only moment
 * anybody is watching — and it is shared, so the browser cannot hold a
 * catalogue the server would have rejected.
 */
export function readCatalogue(parsed: unknown, where: string): Catalogue {
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    throw new Error(`${where} should contain an object of message keys.`);
  }

  const entries = parsed as Record<string, unknown>;
  const missing = MESSAGE_KEYS.filter((key) => typeof entries[key] !== 'string');
  if (missing.length > 0) {
    throw new Error(`${where} is missing: ${missing.join(', ')}`);
  }

  return Object.fromEntries(MESSAGE_KEYS.map((key) => [key, entries[key] as string])) as Catalogue;
}

/** Keys nothing declares. Untidy rather than broken, so it is reported and not thrown. */
export function unknownKeys(parsed: Readonly<Record<string, unknown>>): readonly string[] {
  return Object.keys(parsed).filter((key) => !(MESSAGE_KEYS as readonly string[]).includes(key));
}
